/**
 * Send feature — use-cases (UI-agnostic).
 *
 * Pure orchestration extracted verbatim from the legacy `SendPage`:
 *   - `validateSend`     : recipient / amount / token validation
 *   - `buildSendTxArgs`  : native vs ERC-20 transfer args (single source of
 *                          truth shared by gas-estimate and send)
 *   - `executeSend`      : submit + poll receipt → success / reverted / timeout
 *
 * No React, no DOM. Side effects go through {@link SendRepository}.
 */

import type {
  EstimateGasArgs,
  TransactionReceiptResult,
} from "../../wallet-portfolio/types";
import type { SendAsset } from "../types";
import {
  encodeErc20Transfer,
  isAddress,
  isNativeSendAsset,
  normalizeAddress,
  parseTokenAmount,
} from "../utils";
import type { SendRepository } from "./SendRepository";

export const RECEIPT_POLL_INTERVAL_MS = 1500;
export const RECEIPT_POLL_MAX_ATTEMPTS = 120;

export interface SendFieldErrors {
  token?: string;
  recipient?: string;
  amount?: string;
}

export function hasFieldErrors(errors: SendFieldErrors): boolean {
  return Boolean(errors.token || errors.recipient || errors.amount);
}

// ---------------------------------------------------------------------------
// validateSend
// ---------------------------------------------------------------------------

export interface ValidateSendInput {
  recipient: string;
  amount: string;
  token: SendAsset;
  /** Raw on-chain balance (wei) for the token, used for the over-balance check. */
  balanceRaw: bigint;
}

export interface ValidateSendResult {
  errors: SendFieldErrors;
  /** Parsed amount in wei — present only when the amount is valid. */
  rawAmount?: bigint;
  /** Trimmed, checksum-agnostic recipient — present only when valid. */
  recipient?: `0x${string}`;
}

export function validateSend({
  recipient,
  amount,
  token,
  balanceRaw,
}: ValidateSendInput): ValidateSendResult {
  const errors: SendFieldErrors = {};
  const trimmedRecipient = recipient.trim();
  const parsed = parseTokenAmount(amount, token.quantity.decimals);
  let rawAmount: bigint | undefined;

  if (!trimmedRecipient) {
    errors.recipient = "Enter a recipient address.";
  } else if (!isAddress(trimmedRecipient)) {
    errors.recipient = "Enter a valid EVM recipient address.";
  }

  if ("error" in parsed) {
    errors.amount = parsed.error;
  } else {
    rawAmount = parsed.value;
    if (rawAmount > balanceRaw) {
      errors.amount = "Amount exceeds available balance.";
    }
  }

  if (!isNativeSendAsset(token) && !isAddress(token.address)) {
    errors.token = "Token contract address is invalid.";
  }

  return {
    errors,
    rawAmount: errors.amount ? undefined : rawAmount,
    recipient:
      trimmedRecipient && isAddress(trimmedRecipient)
        ? (trimmedRecipient as `0x${string}`)
        : undefined,
  };
}

// ---------------------------------------------------------------------------
// buildSendTxArgs
// ---------------------------------------------------------------------------

export interface BuildSendTxArgsInput {
  token: SendAsset;
  walletAddress: string;
  recipient: `0x${string}`;
  rawAmount: bigint;
}

export function buildSendTxArgs({
  token,
  walletAddress,
  recipient,
  rawAmount,
}: BuildSendTxArgsInput): EstimateGasArgs {
  const from = walletAddress as `0x${string}`;
  if (isNativeSendAsset(token)) {
    return { to: recipient, value: rawAmount, chainId: token.chainId, from };
  }
  return {
    to: normalizeAddress(token.address),
    data: encodeErc20Transfer(recipient, rawAmount),
    value: 0n,
    chainId: token.chainId,
    from,
  };
}

// ---------------------------------------------------------------------------
// executeSend
// ---------------------------------------------------------------------------

export function isReceiptReverted(receipt: TransactionReceiptResult): boolean {
  const { status } = receipt;
  return (
    status === "reverted" ||
    status === "0x0" ||
    status === 0 ||
    status === 0n ||
    status === false
  );
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Terminal outcome of {@link executeSend} (send-level errors throw instead). */
export type SendExecOutcome = "success" | "reverted" | "timeout";

export interface ExecuteSendInput {
  repository: SendRepository;
  args: EstimateGasArgs;
  chainId: number;
  /**
   * Called once the tx is broadcast (hash known) — before receipt polling.
   * The flow hook uses this to flip into "confirming" and persist the recent
   * address while receipt polling continues.
   */
  onSubmitted: (hash: `0x${string}`) => void;
  pollIntervalMs?: number;
  pollMaxAttempts?: number;
}

/**
 * Submit the transaction and poll for its receipt.
 * Resolves to a terminal outcome; throws on submit / network errors so the
 * caller can format them with transaction context.
 */
export async function executeSend({
  repository,
  args,
  chainId,
  onSubmitted,
  pollIntervalMs = RECEIPT_POLL_INTERVAL_MS,
  pollMaxAttempts = RECEIPT_POLL_MAX_ATTEMPTS,
}: ExecuteSendInput): Promise<SendExecOutcome> {
  const hash = await repository.sendTransaction(args);
  onSubmitted(hash);

  const { getTransactionReceipt } = repository;
  if (!getTransactionReceipt) return "success";

  for (let attempt = 0; attempt < pollMaxAttempts; attempt += 1) {
    const receipt = await getTransactionReceipt({ hash, chainId }).catch(
      () => null,
    );
    if (receipt) {
      return isReceiptReverted(receipt) ? "reverted" : "success";
    }
    await delay(pollIntervalMs);
  }

  return "timeout";
}
